<template>
    <Card pageTitle="POS App Releases">
        <div v-if="canView" class="pos-release-page">
            <div class="pos-release-header">
                <div>
                    <h2>POS App Releases</h2>
                    <p>Upload private APK builds and target them to stores or individual registered POS devices.</p>
                </div>
                <button class="icon-button" type="button" @click="loadData" :disabled="loading" title="Refresh releases">
                    <i class="material-icons">refresh</i>
                </button>
            </div>

            <form v-if="canEdit" class="release-form" @submit.prevent="submitRelease">
                <div class="form-heading">
                    <div>
                        <h3>{{ editingRelease ? "Edit Release" : "Upload Release" }}</h3>
                        <p v-if="editingRelease">Editing v{{ editingRelease.versionName }} build {{ editingRelease.versionCode }}</p>
                        <p v-else>First install the updater-capable APK manually, then upload newer builds here.</p>
                    </div>
                    <button v-if="editingRelease" class="text-button" type="button" @click="resetForm">Cancel edit</button>
                </div>

                <div class="form-grid">
                    <label v-if="!editingRelease" class="field field--file">
                        <span>APK file</span>
                        <input type="file" accept=".apk,application/vnd.android.package-archive" @change="handleFileChange" />
                        <small v-if="form.apkFile">{{ form.apkFile.name }}</small>
                    </label>
                    <label class="field">
                        <span>Version name</span>
                        <input v-model.trim="form.versionName" type="text" :disabled="!!editingRelease" placeholder="1.0.7" />
                    </label>
                    <label class="field">
                        <span>Version code</span>
                        <input v-model.number="form.versionCode" type="number" min="1" :disabled="!!editingRelease" placeholder="11" />
                    </label>
                    <label class="field">
                        <span>Minimum supported build</span>
                        <input v-model.number="form.minSupportedVersionCode" type="number" min="1" placeholder="Optional" />
                    </label>
                    <label class="field">
                        <span>Status</span>
                        <select v-model="form.status">
                            <option value="draft">Draft</option>
                            <option value="active">Active</option>
                            <option value="disabled">Disabled</option>
                        </select>
                    </label>
                </div>

                <label class="field field--full">
                    <span>Release notes</span>
                    <textarea v-model.trim="form.releaseNotes" rows="4" placeholder="Operator-facing changes in this APK"></textarea>
                </label>

                <div class="target-section">
                    <div class="target-heading">
                        <h3>Targets</h3>
                        <span>{{ selectedTargetCount }} selected</span>
                    </div>
                    <div class="target-grid">
                        <div class="target-list">
                            <div class="target-list-heading">
                                <h4>Warehouses</h4>
                                <div class="target-actions">
                                    <button type="button" class="text-button" @click="selectAllWarehouses">All locations</button>
                                    <button type="button" class="text-button" @click="clearWarehouses">Clear</button>
                                </div>
                            </div>
                            <label v-for="warehouse in warehouses" :key="warehouse.id" class="target-row">
                                <input v-model="form.targetWarehouseIds" type="checkbox" :value="String(warehouse.id)" />
                                <span>{{ warehouse.name }}</span>
                            </label>
                            <p v-if="!warehouses.length" class="empty-text">No warehouses loaded.</p>
                        </div>
                        <div class="target-list">
                            <div class="target-list-heading">
                                <h4>Devices</h4>
                                <div class="target-actions">
                                    <button type="button" class="text-button" @click="selectAllDevices">All devices</button>
                                    <button type="button" class="text-button" @click="clearDevices">Clear</button>
                                </div>
                            </div>
                            <label v-for="device in posDevices" :key="device.id" class="target-row">
                                <input v-model="form.targetDeviceIds" type="checkbox" :value="String(device.id)" />
                                <span>{{ device.name }} · {{ device.activationCode }}</span>
                                <small>{{ device.warehouseName || "No warehouse" }}</small>
                            </label>
                            <p v-if="!posDevices.length" class="empty-text">No POS devices loaded.</p>
                        </div>
                    </div>
                </div>

                <div class="form-actions">
                    <Button type="submit" :disabled="!canSubmit || submitting" noMargin>
                        {{ submitting ? "Saving..." : editingRelease ? "Save Changes" : "Upload Release" }}
                    </Button>
                    <Button type="button" borderOnly noMargin @click="resetForm">Clear</Button>
                    <span v-if="activeTargetWarning" class="warning-text">Active releases need at least one target.</span>
                </div>
            </form>

            <div class="release-history">
                <div class="history-heading">
                    <h3>Release History</h3>
                    <span>{{ releases.length }} builds</span>
                </div>
                <div class="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th>Build</th>
                                <th>Status</th>
                                <th>Min Build</th>
                                <th>Targets</th>
                                <th>Rollout</th>
                                <th>File</th>
                                <th>SHA-256</th>
                                <th>Updated</th>
                                <th v-if="canEdit"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="loading">
                                <td :colspan="historyColumnCount" class="empty-cell">Loading releases...</td>
                            </tr>
                            <tr v-else-if="!releases.length">
                                <td :colspan="historyColumnCount" class="empty-cell">No POS app releases uploaded yet.</td>
                            </tr>
                            <template v-else>
                                <template v-for="release in releases">
                                <tr :key="release.id">
                                    <td>
                                        <strong>v{{ release.versionName }}</strong>
                                        <small>Build {{ release.versionCode }}</small>
                                        <p v-if="release.releaseNotes" class="notes">{{ release.releaseNotes }}</p>
                                    </td>
                                    <td>
                                        <span :class="['status-badge', `status-badge--${release.status}`]">
                                            {{ release.status }}
                                        </span>
                                    </td>
                                    <td>{{ release.minSupportedVersionCode || "Optional" }}</td>
                                    <td>
                                        <div class="target-summary">
                                            <span v-for="target in release.targets" :key="`${target.targetType}-${target.targetId}`">
                                                {{ formatTarget(target) }}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="rollout-summary">
                                            <strong>{{ releaseRolloutSummary(release).label }}</strong>
                                            <small>{{ releaseRolloutSummary(release).detail }}</small>
                                            <button
                                                type="button"
                                                class="text-button"
                                                :disabled="!rolloutRows(release).length"
                                                @click="toggleRolloutDetails(release.id)"
                                            >
                                                {{ isRolloutExpanded(release.id) ? "Hide devices" : "View devices" }}
                                            </button>
                                        </div>
                                    </td>
                                    <td>
                                        <span>{{ release.fileName }}</span>
                                        <small>{{ formatBytes(release.fileSizeBytes) }}</small>
                                    </td>
                                    <td>
                                        <code>{{ shortenSha(release.sha256) }}</code>
                                    </td>
                                    <td>{{ formatDate(release.updatedAt || release.createdAt) }}</td>
                                    <td v-if="canEdit" class="action-cell">
                                        <button type="button" class="text-button" @click="startEdit(release)">Edit</button>
                                        <button
                                            v-if="release.status !== 'active'"
                                            type="button"
                                            class="text-button"
                                            :disabled="!canActivateRelease(release)"
                                            :title="
                                                canActivateRelease(release)
                                                    ? 'Activate release'
                                                    : 'Add at least one target before activating'
                                            "
                                            @click="setReleaseStatus(release, 'active')"
                                        >
                                            Activate
                                        </button>
                                        <button
                                            v-if="release.status !== 'disabled'"
                                            type="button"
                                            class="text-button text-button--danger"
                                            @click="setReleaseStatus(release, 'disabled')"
                                        >
                                            Disable
                                        </button>
                                    </td>
                                </tr>
                                <tr
                                    v-if="isRolloutExpanded(release.id)"
                                    :key="`rollout-${release.id}`"
                                    class="rollout-detail-row"
                                >
                                    <td :colspan="historyColumnCount">
                                        <div class="rollout-detail">
                                            <table class="rollout-table">
                                                <thead>
                                                    <tr>
                                                        <th>Device</th>
                                                        <th>Location</th>
                                                        <th>Installed Build</th>
                                                        <th>Last Check-In</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="row in rolloutRows(release)" :key="row.device.id">
                                                        <td>
                                                            <strong>{{ row.device.name || "Unnamed POS" }}</strong>
                                                            <small>{{ row.device.activationCode || "No code" }}</small>
                                                        </td>
                                                        <td>{{ row.device.warehouseName || "No warehouse" }}</td>
                                                        <td>{{ row.buildLabel }}</td>
                                                        <td>{{ row.lastCheckInLabel }}</td>
                                                        <td>
                                                            <span :class="['rollout-badge', `rollout-badge--${row.state}`]">
                                                                {{ row.statusLabel }}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </td>
                                </tr>
                                </template>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { http } from "../../utils/http"
import { formatBusinessInstant } from "../../utils/business-date"

function emptyForm() {
    return {
        apkFile: null,
        versionName: "",
        versionCode: "",
        minSupportedVersionCode: "",
        releaseNotes: "",
        status: "draft",
        targetWarehouseIds: [],
        targetDeviceIds: [],
    }
}

export default {
    components: { Card, Button },
    data() {
        return {
            loading: false,
            submitting: false,
            releases: [],
            devices: [],
            editingRelease: null,
            expandedRolloutReleaseIds: [],
            form: emptyForm(),
        }
    },
    computed: {
        ...mapState("misc", ["warehouses"]),
        ...mapState("auth", ["user"]),
        canView() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_VIEW_DEVICE,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_DEVICE,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        posDevices() {
            return this.devices.filter((device) => (device.deviceType || "").toUpperCase() === "POS")
        },
        historyColumnCount() {
            return this.canEdit ? 9 : 8
        },
        selectedTargetCount() {
            return this.form.targetWarehouseIds.length + this.form.targetDeviceIds.length
        },
        activeTargetWarning() {
            return this.form.status === "active" && this.selectedTargetCount === 0
        },
        canSubmit() {
            if (!this.canEdit) return false
            if (this.submitting) return false
            if (this.activeTargetWarning) return false
            if (this.editingRelease) return true
            return !!this.form.apkFile && !!this.form.versionName && Number(this.form.versionCode) > 0
        },
        warehouseMap() {
            return this.warehouses.reduce((acc, warehouse) => {
                acc[String(warehouse.id)] = warehouse.name
                return acc
            }, {})
        },
        deviceMap() {
            return this.devices.reduce((acc, device) => {
                acc[String(device.id)] = `${device.name} (${device.activationCode})`
                return acc
            }, {})
        },
    },
    mounted() {
        if (this.canView) {
            this.loadData()
        }
    },
    methods: {
        async loadData() {
            if (!this.canView) return
            this.loading = true
            try {
                await this.$store.dispatch("misc/fetchMainMasters", { scope: "locations" })
                const [releaseRes, deviceRes] = await Promise.all([
                    http.get("/pos-app/releases"),
                    http.get("/devices"),
                ])
                this.releases = releaseRes.data?.result || []
                this.devices = deviceRes.data?.result || []
            } catch (err) {
                console.log("error loading POS releases", err)
                this.$toast.error(err?.response?.data?.message || "Could not load POS app releases.")
            }
            this.loading = false
        },
        handleFileChange(event) {
            const file = event.target.files && event.target.files[0]
            this.form.apkFile = file || null
        },
        resetForm() {
            this.editingRelease = null
            this.form = emptyForm()
        },
        selectAllWarehouses() {
            this.form.targetWarehouseIds = this.warehouses.map((warehouse) => String(warehouse.id))
        },
        clearWarehouses() {
            this.form.targetWarehouseIds = []
        },
        selectAllDevices() {
            this.form.targetDeviceIds = this.posDevices.map((device) => String(device.id))
        },
        clearDevices() {
            this.form.targetDeviceIds = []
        },
        hasTargets(release) {
            return (release.targets || []).length > 0
        },
        canActivateRelease(release) {
            return this.canEdit && this.hasTargets(release)
        },
        startEdit(release) {
            if (!this.canEdit) return
            this.editingRelease = release
            this.form = {
                apkFile: null,
                versionName: release.versionName,
                versionCode: release.versionCode,
                minSupportedVersionCode: release.minSupportedVersionCode || "",
                releaseNotes: release.releaseNotes || "",
                status: release.status || "draft",
                targetWarehouseIds: (release.targets || [])
                    .filter((target) => target.targetType === "warehouse")
                    .map((target) => String(target.targetId)),
                targetDeviceIds: (release.targets || [])
                    .filter((target) => target.targetType === "device")
                    .map((target) => String(target.targetId)),
            }
        },
        buildPayload() {
            return {
                status: this.form.status,
                minSupportedVersionCode: this.form.minSupportedVersionCode || null,
                releaseNotes: this.form.releaseNotes || "",
                targetWarehouseIds: this.form.targetWarehouseIds,
                targetDeviceIds: this.form.targetDeviceIds,
            }
        },
        async submitRelease() {
            if (!this.canSubmit) return
            this.submitting = true
            try {
                if (this.editingRelease) {
                    await http.patch(`/pos-app/releases/${this.editingRelease.id}`, this.buildPayload())
                    this.$toast.success("POS app release updated.")
                } else {
                    const data = new FormData()
                    data.append("apkFile", this.form.apkFile)
                    data.append("versionName", this.form.versionName)
                    data.append("versionCode", this.form.versionCode)
                    if (this.form.minSupportedVersionCode) {
                        data.append("minSupportedVersionCode", this.form.minSupportedVersionCode)
                    }
                    data.append("releaseNotes", this.form.releaseNotes || "")
                    data.append("status", this.form.status)
                    data.append("targetWarehouseIds", JSON.stringify(this.form.targetWarehouseIds))
                    data.append("targetDeviceIds", JSON.stringify(this.form.targetDeviceIds))
                    await http.post("/pos-app/releases", data, {
                        headers: { "Content-Type": "multipart/form-data" },
                    })
                    this.$toast.success("POS app release uploaded.")
                }
                this.resetForm()
                await this.loadData()
            } catch (err) {
                console.log("error saving POS release", err)
                this.$toast.error(err?.response?.data?.message || "Could not save POS app release.")
            }
            this.submitting = false
        },
        async setReleaseStatus(release, status) {
            if (!this.canEdit) return
            if (status === "active" && !this.hasTargets(release)) {
                this.$toast.error("Select at least one target before activating.")
                return
            }
            try {
                await http.patch(`/pos-app/releases/${release.id}`, { status })
                this.$toast.success(status === "active" ? "Release activated." : "Release disabled.")
                await this.loadData()
            } catch (err) {
                console.log("error updating release status", err)
                this.$toast.error(err?.response?.data?.message || "Could not update release status.")
            }
        },
        formatBytes(value) {
            const bytes = Number(value || 0)
            if (!bytes) return "0 B"
            const units = ["B", "KB", "MB", "GB"]
            const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
            return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`
        },
        shortenSha(value = "") {
            return value.length > 18 ? `${value.slice(0, 12)}...${value.slice(-6)}` : value
        },
        formatDate(value) {
            if (!value) return ""
            return formatBusinessInstant(value)
        },
        formatTarget(target) {
            if (target.targetType === "warehouse") {
                return this.warehouseMap[String(target.targetId)] || `Warehouse ${target.targetId}`
            }
            return this.deviceMap[String(target.targetId)] || `Device ${target.targetId}`
        },
        releaseTargetsDevice(release, device) {
            const targets = release.targets || []
            return targets.some((target) => {
                const targetType = String(target.targetType || "")
                const targetId = String(target.targetId || "")
                if (targetType === "device") return targetId === String(device.id)
                if (targetType === "warehouse") return targetId === String(device.warehouseId)
                return false
            })
        },
        targetedDevicesForRelease(release) {
            return this.posDevices.filter((device) => this.releaseTargetsDevice(release, device))
        },
        buildLabelForDevice(device) {
            if (!device.appVersionCode) return "Not reported"
            return `${device.appVersionName || "Unknown"} (${device.appVersionCode})`
        },
        rolloutStateForDevice(release, device) {
            const deviceBuild = Number(device.appVersionCode || 0)
            const releaseBuild = Number(release.versionCode || 0)
            if (!deviceBuild) {
                return {
                    state: "unknown",
                    statusLabel: "Not reported",
                }
            }
            if (deviceBuild >= releaseBuild) {
                return {
                    state: "updated",
                    statusLabel: "Updated",
                }
            }
            return {
                state: "pending",
                statusLabel: "Pending update",
            }
        },
        rolloutRows(release) {
            return this.targetedDevicesForRelease(release).map((device) => {
                const state = this.rolloutStateForDevice(release, device)
                return {
                    device,
                    ...state,
                    buildLabel: this.buildLabelForDevice(device),
                    lastCheckInLabel: device.lastAppCheckAtFormatted || "No check-in",
                }
            })
        },
        releaseRolloutSummary(release) {
            const rows = this.rolloutRows(release)
            if (!rows.length) {
                return {
                    label: "No targeted devices",
                    detail: "Add location or device targets",
                }
            }
            const updated = rows.filter((row) => row.state === "updated").length
            const pending = rows.filter((row) => row.state === "pending").length
            const unknown = rows.filter((row) => row.state === "unknown").length
            return {
                label: `${updated}/${rows.length} updated`,
                detail: `${pending} pending · ${unknown} not reported`,
            }
        },
        isRolloutExpanded(releaseId) {
            return this.expandedRolloutReleaseIds.includes(String(releaseId))
        },
        toggleRolloutDetails(releaseId) {
            const normalizedReleaseId = String(releaseId)
            if (this.isRolloutExpanded(normalizedReleaseId)) {
                this.expandedRolloutReleaseIds = this.expandedRolloutReleaseIds.filter(
                    (id) => id !== normalizedReleaseId
                )
                return
            }
            this.expandedRolloutReleaseIds = this.expandedRolloutReleaseIds.concat(normalizedReleaseId)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.pos-release-page {
    display: grid;
    gap: 22px;
    text-align: left;
}

.pos-release-header,
.form-heading,
.target-heading,
.history-heading,
.form-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
}

h2,
h3,
h4,
p {
    margin: 0;
}

h2 {
    color: #111827;
    font-size: 24px;
    font-weight: 800;
}

h3 {
    color: #0d2d2c;
    font-size: 16px;
    font-weight: 800;
}

h4 {
    color: rgba($textColor, 0.78);
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
}

p {
    color: rgba($textColor, 0.68);
    font-size: 13px;
    line-height: 1.4;
}

.release-form,
.release-history {
    display: grid;
    gap: 16px;
    padding-top: 18px;
    border-top: 1px solid $lightBorderColor;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
}

.field {
    display: grid;
    gap: 6px;
    min-width: 0;

    span {
        color: rgba($textColor, 0.75);
        font-size: 12px;
        font-weight: 800;
        text-transform: uppercase;
    }

    input,
    select,
    textarea {
        width: 100%;
        min-height: 40px;
        border: 1px solid $lightBorderColor;
        border-radius: 6px;
        padding: 8px 10px;
        color: $textColor;
        background: #fff;
    }

    textarea {
        resize: vertical;
    }

    small {
        color: rgba($textColor, 0.62);
        font-size: 12px;
        overflow-wrap: anywhere;
    }
}

.field--full {
    width: 100%;
}

.field--file {
    grid-column: span 2;
}

.target-section {
    display: grid;
    gap: 10px;
}

.target-heading span,
.history-heading span {
    color: rgba($textColor, 0.65);
    font-size: 12px;
    font-weight: 700;
}

.target-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.target-list {
    display: grid;
    align-content: start;
    gap: 8px;
    max-height: 260px;
    overflow: auto;
    padding: 12px;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    background: rgba(13, 45, 44, 0.025);
}

.target-list-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.target-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
}

.target-row {
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr);
    gap: 8px;
    align-items: start;
    color: $textColor;
    font-size: 13px;

    input {
        margin-top: 2px;
    }

    small {
        grid-column: 2;
        color: rgba($textColor, 0.58);
        font-size: 12px;
    }
}

.form-actions {
    justify-content: flex-start;
}

.warning-text {
    color: #a16207;
    font-size: 13px;
    font-weight: 700;
}

.icon-button {
    width: 38px;
    height: 38px;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    background: #fff;
    color: $primaryColor;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.text-button {
    border: 0;
    background: transparent;
    color: #2563eb;
    cursor: pointer;
    font-size: 13px;
    font-weight: 800;
    padding: 4px 0;
}

.text-button--danger {
    color: #b91c1c;
}

.table-wrap {
    width: 100%;
    overflow: auto;
}

table {
    width: 100%;
    border-collapse: collapse;
    min-width: 1240px;
}

th,
td {
    padding: 12px 10px;
    border-bottom: 1px solid $lightBorderColor;
    vertical-align: top;
    text-align: left;
    font-size: 13px;
}

th {
    color: rgba($textColor, 0.7);
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    white-space: nowrap;
}

td strong,
td span,
td code {
    display: block;
}

td small {
    display: block;
    color: rgba($textColor, 0.58);
    margin-top: 3px;
}

.notes {
    max-width: 260px;
    margin-top: 6px;
}

.status-badge {
    display: inline-flex;
    width: fit-content;
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
}

.status-badge--active {
    color: #065f46;
    background: #d1fae5;
}

.status-badge--draft {
    color: #374151;
    background: #e5e7eb;
}

.status-badge--disabled {
    color: #991b1b;
    background: #fee2e2;
}

.target-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-width: 280px;

    span {
        padding: 3px 7px;
        border-radius: 999px;
        background: rgba(37, 99, 235, 0.08);
        color: #1f3b71;
        font-size: 12px;
    }
}

.rollout-summary {
    display: grid;
    gap: 3px;
    min-width: 140px;

    strong {
        color: $textColor;
        font-size: 13px;
    }

    .text-button {
        justify-self: start;
    }
}

.rollout-detail {
    padding: 6px 0;
    position: sticky;
    left: 0;
    overflow-x: auto;
    width: min(100%, calc(100vw - 380px));
}

.rollout-table {
    min-width: 760px;
    background: rgba(13, 45, 44, 0.018);

    th,
    td {
        padding: 9px 10px;
    }
}

.rollout-detail-row td {
    background: rgba(13, 45, 44, 0.035);
}

.rollout-badge {
    display: inline-flex;
    width: fit-content;
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
}

.rollout-badge--updated {
    color: #065f46;
    background: #d1fae5;
}

.rollout-badge--pending {
    color: #92400e;
    background: #fef3c7;
}

.rollout-badge--unknown {
    color: #374151;
    background: #e5e7eb;
}

code {
    max-width: 160px;
    color: #334155;
    font-size: 12px;
    overflow-wrap: anywhere;
}

.action-cell {
    display: grid;
    gap: 6px;
}

.empty-cell,
.empty-text {
    color: rgba($textColor, 0.6);
    font-size: 13px;
}

.empty-cell {
    text-align: center;
    padding: 30px 10px;
}

@media (max-width: 920px) {
    .form-grid,
    .target-grid {
        grid-template-columns: 1fr;
    }

    .field--file {
        grid-column: auto;
    }

    .rollout-detail {
        width: calc(100vw - 56px);
    }
}
</style>
